<template>
  <div class="psCriteriaControl">
    <el-date-picker
      v-if="field.type === 'month'"
      v-model="model"
      type="month"
      value-format="yyyy-MM"
      format="MMMM yyyy"
      size="mini"
      :clearable="Boolean(field.clearable)">
    </el-date-picker>
    <el-date-picker
      v-else-if="field.type === 'date'"
      v-model="model"
      type="date"
      value-format="yyyy-MM-dd"
      :format="field.format || 'dd/MM/yyyy'"
      size="mini"
      :clearable="Boolean(field.clearable)"
      :placeholder="field.placeholder">
    </el-date-picker>
    <el-date-picker
      v-else-if="field.type === 'date_range'"
      v-model="model"
      type="daterange"
      value-format="yyyy-MM-dd"
      :format="field.format || 'dd/MM/yyyy'"
      size="mini"
      :clearable="Boolean(field.clearable)"
      :start-placeholder="field.startPlaceholder || 'Desde'"
      :end-placeholder="field.endPlaceholder || 'Hasta'"
      range-separator="-">
    </el-date-picker>
    <el-input-number
      v-else-if="field.type === 'input_number'"
      v-model="model"
      size="mini"
      :min="field.min"
      :max="field.max"
      :step="field.step || 1"
      :controls="field.controls !== false"
      :placeholder="field.placeholder">
    </el-input-number>
    <el-select
      v-else-if="field.type === 'select'"
      v-model="model"
      size="mini"
      :multiple="Boolean(field.multiple)"
      :filterable="Boolean(field.filterable || remoteFunction)"
      :clearable="Boolean(field.clearable)"
      :remote="Boolean(remoteFunction)"
      :remote-method="remoteSearch"
      :loading="remoteLoading"
      :value-key="field.fieldKey || 'value'"
      :placeholder="field.placeholder">
      <el-option
        v-for="(option, index) in displayedOptions"
        :key="optionKey(option, index)"
        :label="optionLabel(option)"
        :value="optionValue(option)">
      </el-option>
    </el-select>
    <el-autocomplete
      v-else-if="field.type === 'autocomplete'"
      v-model="model"
      size="mini"
      :fetch-suggestions="autocompleteSearch"
      :value-key="field.fieldLabel || 'value'"
      :clearable="Boolean(field.clearable)"
      :placeholder="field.placeholder">
    </el-autocomplete>
    <div v-else-if="field.type === 'select_input'" class="selectInput">
      <el-select
        :value="selectInputValue.select"
        size="mini"
        :clearable="Boolean(field.clearable)"
        :filterable="Boolean(field.filterable)"
        :placeholder="field.selectPlaceholder"
        @input="setSelectInput('select', $event)">
        <el-option
          v-for="(option, index) in displayedOptions"
          :key="optionKey(option, index)"
          :label="optionLabel(option)"
          :value="optionValue(option)">
        </el-option>
      </el-select>
      <el-input
        :value="selectInputValue.input"
        size="mini"
        :placeholder="field.inputPlaceholder || field.placeholder"
        @input="setSelectInput('input', $event)">
      </el-input>
    </div>
    <el-switch
      v-else-if="field.type === 'switch'"
      v-model="model"
      :active-value="field.activeValue === undefined ? true : field.activeValue"
      :inactive-value="field.inactiveValue === undefined ? false : field.inactiveValue"
      :active-text="field.activeText"
      :inactive-text="field.inactiveText">
    </el-switch>
    <el-input
      v-else
      v-model="model"
      size="mini"
      :clearable="Boolean(field.clearable)"
      :placeholder="field.placeholder">
    </el-input>
  </div>
</template>

<script>
import { invokeOptionsProvider } from '@/shared/criteria/criteria_remote'

export default {
  name: 'PsCriteriaControl',
  props: {
    field: { type: Object, required: true },
    value: { required: false }
  },
  data () {
    return {
      remoteOptions: [],
      remoteLoading: false,
      remoteSequence: 0
    }
  },
  computed: {
    model: {
      get () { return this.value },
      set (value) { this.$emit('input', value) }
    },
    remoteFunction () {
      return this.field.fetchOptions || this.field.searchFn
    },
    displayedOptions () {
      return this.remoteFunction ? this.remoteOptions : (this.field.options || [])
    },
    selectInputValue () {
      return this.value && typeof this.value === 'object' ? this.value : { select: '', input: '' }
    }
  },
  methods: {
    optionLabel (option) {
      if (option && typeof option === 'object') return option[this.field.fieldLabel || 'label']
      return option
    },
    optionValue (option) {
      if (option && typeof option === 'object' && !this.field.returnObject) return option[this.field.fieldKey || 'value']
      return option
    },
    optionKey (option, index) {
      const value = option && typeof option === 'object' ? option[this.field.fieldKey || 'value'] : option
      return value === undefined ? index : value
    },
    async requestOptions (term) {
      const sequence = ++this.remoteSequence
      const minChars = this.field.minChars === undefined ? 0 : this.field.minChars
      if (String(term || '').length < minChars) {
        this.remoteOptions = []
        this.remoteLoading = false
        return []
      }
      this.remoteLoading = true
      try {
        const result = await invokeOptionsProvider(this.remoteFunction, term || '', this.field)
        if (sequence !== this.remoteSequence) return this.remoteOptions
        const options = Array.isArray(result) ? result : result && Array.isArray(result.data) ? result.data : []
        this.remoteOptions = options
        return options
      } catch (error) {
        if (sequence === this.remoteSequence) {
          this.remoteOptions = []
          this.$emit('remote-error', error)
        }
        return []
      } finally {
        if (sequence === this.remoteSequence) this.remoteLoading = false
      }
    },
    remoteSearch (term) {
      if (this.remoteFunction) this.requestOptions(term)
    },
    autocompleteSearch (term, callback) {
      if (!this.remoteFunction) {
        const normalized = String(term || '').toLowerCase()
        callback((this.field.options || []).filter(option => String(this.optionLabel(option) || '').toLowerCase().indexOf(normalized) !== -1))
        return
      }
      this.requestOptions(term).then(callback)
    },
    setSelectInput (key, value) {
      this.$emit('input', Object.assign({}, this.selectInputValue, { [key]: value }))
    }
  },
  mounted () {
    if (this.remoteFunction && (this.field.minChars === undefined || this.field.minChars === 0)) this.requestOptions('')
  },
  beforeDestroy () {
    this.remoteSequence++
  }
}
</script>

<style scoped>
.psCriteriaControl, .psCriteriaControl > .el-select, .psCriteriaControl > .el-input, .psCriteriaControl > .el-date-editor, .psCriteriaControl > .el-input-number { width: 100%; }
.selectInput { display: flex; gap: 6px; }
.selectInput .el-select { width: 42%; }
.selectInput .el-input { flex: 1; }
</style>
